import React from 'react';
import {
  AbsoluteFill,
  Img,
  Sequence,
  interpolate,
  staticFile,
  useCurrentFrame,
} from 'remotion';
import {brand, brandFont} from '../brand';
import {EyebrowPill} from '../components/EyebrowPill';
import {GlassCard} from '../components/GlassCard';
import {PageDots} from '../components/PageDots';
import {Story1Prompt} from '../components/onboarding/Story1Prompt';
import {Story2TimerDial} from '../components/onboarding/Story2TimerDial';
import {Story3ManualBuilder} from '../components/onboarding/Story3ManualBuilder';

type Slide = {
  id: 'auth-hero' | 'live-timer' | 'manual-create';
  eyebrow: string;
  title: string;
  bg: string;
  accent: string;
  overlay: React.ReactNode;
  light: boolean;
};

const SLIDE_FRAMES = 150;
const FADE_FRAMES = 14;

const SLIDES: Slide[] = [
  {
    id: 'auth-hero',
    eyebrow: 'Text/ Photo to Timer',
    title: 'Write a workout note.\nGenerate the timer instantly.',
    bg: 'images/auth_runner_hero.jpg',
    accent: '#DCE4EB',
    overlay: <Story1Prompt />,
    light: false,
  },
  {
    id: 'live-timer',
    eyebrow: 'Learns your style',
    title: 'It remembers and self-tunes\nfor next time.',
    bg: 'images/onboarding_live_timer.jpg',
    accent: brand.primaryLight,
    overlay: <Story2TimerDial />,
    light: true,
  },
  {
    id: 'manual-create',
    eyebrow: 'Manual control',
    title: 'Build the timer\nexactly how you want.',
    bg: 'images/onboarding_manual.jpg',
    accent: brand.primaryLight,
    overlay: <Story3ManualBuilder />,
    light: true,
  },
];

const SlideView: React.FC<{slide: Slide; index: number; total: number}> = ({
  slide,
  index,
  total,
}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, FADE_FRAMES], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const fadeOut = interpolate(
    frame,
    [SLIDE_FRAMES - FADE_FRAMES, SLIDE_FRAMES],
    [1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const opacity = Math.min(fadeIn, fadeOut);

  const scale = interpolate(frame, [0, SLIDE_FRAMES], [1.02, 1.06]);

  return (
    <AbsoluteFill style={{opacity}}>
      <AbsoluteFill style={{background: '#000'}}>
        <Img
          src={staticFile(slide.bg)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${scale})`,
          }}
        />
      </AbsoluteFill>

      <AbsoluteFill
        style={{
          background: slide.light
            ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.02) 24%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.18) 74%, rgba(0,0,0,0.48) 100%)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.22) 18%, rgba(0,0,0,0.46) 44%, rgba(0,0,0,0.72) 72%, rgba(0,0,0,0.94) 100%)',
        }}
      />

      <AbsoluteFill
        style={{
          padding: '120px 60px 100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <EyebrowPill label={slide.eyebrow} />

        <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', marginTop: 60}}>
          <div style={{width: 760}}>
            <GlassCard accentColor={slide.accent} padding={32}>
              {slide.overlay}
            </GlassCard>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 28,
            width: '100%',
          }}
        >
          <div
            style={{
              fontFamily: brandFont,
              fontSize: 56,
              fontWeight: 700,
              color: brand.textPrimary,
              lineHeight: 1.1,
              textAlign: 'center',
              maxWidth: 880,
              whiteSpace: 'pre-line',
              textShadow: '0 6px 24px rgba(0,0,0,0.65)',
              letterSpacing: -0.5,
            }}
          >
            {slide.title}
          </div>
          <PageDots count={total} activeIndex={index} />
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

export const OnboardingShowcase: React.FC = () => (
  <AbsoluteFill style={{background: brand.bg}}>
    {SLIDES.map((slide, i) => (
      <Sequence
        key={slide.id}
        from={i * (SLIDE_FRAMES - FADE_FRAMES)}
        durationInFrames={SLIDE_FRAMES}
      >
        <SlideView slide={slide} index={i} total={SLIDES.length} />
      </Sequence>
    ))}
  </AbsoluteFill>
);
